import { addEventListenersForUndoRedo } from '@/utils/undoRedoManager/utils.js';

const BORDER_SCALING_FACTOR = 2;

/**
 * Calculates the stroke starting position, ending position and dash length so it occupies
 *  the entire given space with an even distribution
 * @param {number} spacing total available space to draw the stroke
 * @param {number} strokeLength target stroke length
 */
const calculateStrokePosition = (spacing, strokeLength) => {
    let count = spacing / strokeLength + 2;
    count = Math.floor(count);
    if (count > 0 && count % 2 === 0) {
        count -= 1;
        strokeLength += strokeLength / count;
    }

    const scalingFactor = spacing / strokeLength / Math.floor(spacing / strokeLength);
    strokeLength *= scalingFactor;
    spacing -= strokeLength * 2;

    if (spacing < 0) {
        spacing = 0;
    }

    return {
        startOffset: strokeLength,
        endOffset: spacing,
        strokeLength: strokeLength
    };
};

/**
 * @private
 * @param {CanvasRenderingContext2D} ctx Context to render on
 */
fabric.Object.prototype._renderStrokeOld = function (ctx) {
    if (!this.stroke || this.strokeWidth === 0) {
        return;
    }
    if (this.shadow && !this.shadow.affectStroke) {
        this._removeShadow(ctx);
    }
    ctx.save();
    if (this.strokeUniform) {
        ctx.scale(1 / this.scaleX, 1 / this.scaleY);
    }

    if (this.strokeDashArray && this.strokeDashArray.length && this.centerDashArray) {
        const largestSegment = Math.max(...this.strokeDashArray);

        ctx.beginPath();
        let strokeProps;

        // Draw a line around each corner first
        // Move to the top-left corner
        let cornerX = -this.width / 2;
        let cornerY = -this.height / 2;
        ctx.moveTo(cornerX, cornerY);
        ctx.lineTo(cornerX, cornerY + largestSegment / 2);
        ctx.moveTo(cornerX, cornerY);
        ctx.lineTo(cornerX + largestSegment / 2, cornerY);
        strokeProps = calculateStrokePosition(this.width - largestSegment, largestSegment);
        fabric.util.drawDashedLine(
            ctx,
            cornerX + largestSegment / 2 + strokeProps.startOffset,
            cornerY,
            cornerX + largestSegment / 2 + strokeProps.startOffset + strokeProps.endOffset,
            cornerY,
            [strokeProps.strokeLength, strokeProps.strokeLength]
        );

        // Move to the top-right corner
        cornerX = this.width / 2;
        cornerY = -this.height / 2;
        ctx.moveTo(cornerX, cornerY);
        ctx.lineTo(cornerX, cornerY + largestSegment / 2);
        ctx.moveTo(cornerX, cornerY);
        ctx.lineTo(cornerX - largestSegment / 2, cornerY);
        strokeProps = calculateStrokePosition(this.height - largestSegment, largestSegment);
        fabric.util.drawDashedLine(
            ctx,
            cornerX,
            cornerY + largestSegment / 2 + strokeProps.startOffset,
            cornerX,
            cornerY + largestSegment / 2 + strokeProps.startOffset + strokeProps.endOffset,
            [strokeProps.strokeLength, strokeProps.strokeLength]
        );

        // Move to the bottom-left corner
        cornerX = -this.width / 2;
        cornerY = this.height / 2;
        ctx.moveTo(cornerX, cornerY);
        ctx.lineTo(cornerX, cornerY - largestSegment / 2);
        ctx.moveTo(cornerX, cornerY);
        ctx.lineTo(cornerX + largestSegment / 2, cornerY);
        strokeProps = calculateStrokePosition(this.height - largestSegment, largestSegment);
        fabric.util.drawDashedLine(
            ctx,
            cornerX,
            cornerY - largestSegment / 2 - strokeProps.startOffset,
            cornerX,
            cornerY - largestSegment / 2 - strokeProps.startOffset - strokeProps.endOffset,
            [strokeProps.strokeLength, strokeProps.strokeLength]
        );

        // Move to the bottom-right corner
        cornerX = this.width / 2;
        cornerY = this.height / 2;
        ctx.moveTo(cornerX, cornerY);
        ctx.lineTo(cornerX, cornerY - largestSegment / 2);
        ctx.moveTo(cornerX, cornerY);
        ctx.lineTo(cornerX - largestSegment / 2, cornerY);
        strokeProps = calculateStrokePosition(this.width - largestSegment, largestSegment);
        fabric.util.drawDashedLine(
            ctx,
            cornerX - largestSegment / 2 - strokeProps.startOffset,
            cornerY,
            cornerX - largestSegment / 2 - strokeProps.startOffset - strokeProps.endOffset,
            cornerY,
            [strokeProps.strokeLength, strokeProps.strokeLength]
        );

        ctx.closePath();
    } else {
        this._setLineDash(ctx, this.strokeDashArray, this._renderDashedStroke);
    }

    if (this.stroke.toLive && this.stroke.gradientUnits === 'percentage') {
        // need to transform gradient in a pattern.
        // this is a slow process. If you are hitting this codepath, and the object
        // is not using caching, you should consider switching it on.
        // we need a canvas as big as the current object caching canvas.
        this._applyPatternForTransformedGradient(ctx, this.stroke);
    } else {
        this._applyPatternGradientTransform(ctx, this.stroke);
    }
    ctx.stroke();
    ctx.restore();
};

/**
 * @private
 * @param {CanvasRenderingContext2D} ctx Context to render on
 */
/*fabric.Object.prototype._renderStroke = function (ctx) {
    if (!this.stroke || this.strokeWidth === 0) {
        return;
    }

    if (this.shadow && !this.shadow.affectStroke) {
        this._removeShadow(ctx);
    }

    ctx.save();
    if (this.strokeUniform && this.group) {
        var scaling = this.getObjectScaling();
        ctx.scale(1 / scaling.scaleX, 1 / scaling.scaleY);
    } else if (this.strokeUniform) {
        ctx.scale(1 / this.scaleX, 1 / this.scaleY);
    }
    this._setLineDash(ctx, this.strokeDashArray);
    this._setStrokeStyles(ctx, this);
    ctx.stroke();
    ctx.restore();
};*/

// Add lock property to objects
fabric.Object.prototype.cacheProperties = fabric.Object.prototype.cacheProperties.concat('locked', 'groupId');
const originalSet = fabric.Object.prototype._set;
fabric.Object.prototype._set = function (key, value) {
    const returnValue = originalSet.apply(this, [key, value]);
    if (key === 'locked' || key === 'dirty' || key === 'groupId' || key === 'canvas' || key === 'pageEpsId') {
        this.updateLockStatus();
    }

    return returnValue;
};

fabric.Object.prototype.updateLockStatus = function () {
    if (this?.excludeFromUpdateLock) {
        return;
    }
    // Lock the element if the element itself is locked OR if the group this element belongs to is locked
    const shouldLockByGroup = !!(
        this.groupId && this.canvas?.getElementsGroups().find((g) => g.id === this.groupId)?.locked
    );
    const shouldLockByPageEps = !!(
        this.pageEpsId && this.canvas?.getElementsPageEps().find((space) => space.id === this.pageEpsId)?.locked
    );
    const shouldLockElement = shouldLockByGroup || shouldLockByPageEps || this.locked;

    this.lockedByGroup = shouldLockByGroup;
    this.lockedByPageEps = shouldLockByPageEps;

    // Only change if lock value is different
    if (this.evented !== !shouldLockElement) {
        this.callSuper('_set', 'evented', !shouldLockElement);
        this.callSuper('_set', 'selectable', !shouldLockElement);
        this.callSuper('_set', 'hasControls', !shouldLockElement);

        switch (this.type) {
            case 'bounded-text':
                if (shouldLockElement && !this.isPatternReplica) {
                    this._backupStroke = this.getStroke();
                    this.setStroke('#ff0000');
                } else if (!shouldLockElement) {
                    this.setStroke(this._backupStroke || '#000000');
                }
                break;
            case 'text-transform':
                if (shouldLockElement) {
                    this._backupBoundingColor = this.getBoundingColor();
                    this.setBoundingColor('rgba(255, 0, 0, 0.3)');
                } else {
                    this.setBoundingColor(this._backupBoundingColor || 'rgba(0, 0, 0, 0.3)');
                }
                break;
            case 'image-placeholder':
            case 'vector-placeholder':
                if (shouldLockElement && !this.isPatternReplica) {
                    this._backupStroke = this.stroke;
                    this.set('stroke', '#ff0000');
                } else if (!shouldLockElement) {
                    this.set('stroke', this._backupStroke || '#000000');
                }
                break;
        }
    }
};

const originalCalcCoords = fabric.Object.prototype.calcCoords;
fabric.Object.prototype.calcCoords = function (absolute) {
    let vptBackup = null;

    // Change the viewport transform when calculating control coordinates to consider the artwork position within the workspace
    if (this.canvas && this.canvas.viewportTransform) {
        vptBackup = this.canvas.viewportTransform.slice(0);
        this.canvas.viewportTransform[4] = this.canvas.viewportTransform[4] + this.canvas._artworkOffsetX;
        this.canvas.viewportTransform[5] = this.canvas.viewportTransform[5] + this.canvas._artworkOffsetY;
    }

    const result = originalCalcCoords.apply(this, [absolute]);
    if (vptBackup) {
        this.canvas.viewportTransform = vptBackup;
    }

    return result;
};

const IGNORE_OBJECTS = ['pathMask', 'rect', 'path'];
const originalInitialize = fabric.Object.prototype.initialize;
fabric.Object.prototype.initialize = function () {
    this.editedProps = [];
    // Is the element itself locked?
    this.locked = false;
    // Is the element locked not by itself but by the group it belongs to?
    this.lockedByGroup = false;
    this.lockedByPageEps = false;
    this.visible = true;
    this.groupId = null;
    this.excludeFromUpdateLock = false;

    addEventListenersForUndoRedo(this);

    const returnValue = originalInitialize.apply(this, arguments);

    if (IGNORE_OBJECTS.includes(this.type)) {
        this.excludeFromUpdateLock = true;
    }
    this.updateLockStatus();
    return returnValue;
};

fabric.Object.prototype.isEdited = function () {
    return !!this.editedProps.length;
};

/*
 * Set the edited property of the object
 * @param {string} properties - The properties to set as edited
 */
fabric.Object.prototype.setEditedProp = function (...properties) {
    for (const prop of properties) {
        if (prop && !this.editedProps.includes(prop)) {
            this.editedProps.push(prop);
        }
    }
};

fabric.Object.prototype.getFlip = function () {
    return {
        flipX: this.item(0).flipX || false,
        flipY: this.item(0).flipY || false
    };
};

fabric.Object.prototype.setFlip = function (flipX, flipY) {
    try {
        this.item(0).set('flipX', flipX || false);
        this.item(0).set('flipY', flipY || false);
    } catch (e) {
        console.error(e);
    }
};

fabric.Object.prototype.setColor = function (color) {
    this.set('fill', color);
};

fabric.Object.prototype.isNotVisible = function () {
    return (
        this.opacity === 0 ||
        (this.width === 0 && this.height === 0 && this.strokeWidth === 0) ||
        !this.visible ||
        (this.canvas &&
            this.canvas.getElementsGroups &&
            this.canvas.getElementsGroups().find((g) => g.id === this.groupId)?.visible === false) ||
        (this.canvas &&
            this.canvas.getElementsPageEps &&
            this.canvas.getElementsPageEps().find((s) => s.id === this.pageEpsId)?.visible === false)
    );
};

fabric.Object.prototype.borderScaleFactor = BORDER_SCALING_FACTOR;
fabric.Object.prototype.borderColor = 'rgba(79,128,255,1)';
fabric.Object.prototype.centerDashArray = true;
